import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore, Store } from '@ngrx/store';
import { provideMissingTranslationHandler, provideTranslateService, TranslateService } from '@ngx-translate/core';
import { routes } from './app.routes';

import { provideDateFnsAdapter, } from '@angular/material-date-fns-adapter';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { OidcSecurityService, provideAuth } from 'angular-auth-oidc-client';
import { Chart, registerables } from 'chart.js';
import { environment } from '../environments/environment';
import { AuthenticationFactory } from './factories/authentication.factory';
import { customMatIconsInitializerFactory } from './factories/custom-mat-Icons-initializer.factory';
import { EmptyMissingTranslationHandler } from './factories/EmptyMissingTranslationHandler';
import { languageInitializerFactory } from './factories/language-initializer.factory';
import { authenticationInterceptor } from './Intercepters/authentication/authentication.interceptor';
import { loadingBarInterceptor } from './Intercepters/loading-bar.interceptor';
import { AuthTokenService } from './services/auth-token.service';
import { InstallationStateEffects } from './state/installations-state/installation-state.effects';
import { installationStateReducer } from './state/installations-state/installation-state.reducers';
import { mainFilterReducer } from './state/list-features/list-featues.reducers';
import { ListFeatureEffects } from './state/list-features/list-features.effects';
import { GeneralStateEffects } from './state/pageState/page-state.effects';
import { generalStateReducere } from './state/pageState/page-state.reducers';
import { ProfileStateEffect } from './state/profile-state/profile-state-effect';
import { profileStateReducere } from './state/profile-state/profile-state.reducers';
import { RouterEffects } from './state/routeState/router.effects';
import { SecurityStateEffects } from './state/security-state/security-state.effects';
import { securityStateReducere } from './state/security-state/security-state.reducers';
import { BreeceSelectorEffects } from './state/selector-state/breece-selector.effects';
import { breeceSelectorReducer } from './state/selector-state/breece-selector.reducers';
import { TableFilterSetEffects } from './state/table-state/table-filter-set-state/table-filter-set.effects';
import { TableFilterEffects } from './state/table-state/table-filter-state/table-filter.effects';
import { tableStateReducer } from './state/table-state/table-reducer';
import { TableViewEffects } from './state/table-state/table-view.effects';

Chart.register(...registerables);
export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withXhr(), withInterceptors([authenticationInterceptor, loadingBarInterceptor])),
    provideTranslateService({
      fallbackLang: 'en-GB',
      loader: provideTranslateHttpLoader({ prefix: "./assets/i18n/", suffix: ".json", enforceLoading: true }),
      missingTranslationHandler: provideMissingTranslationHandler(EmptyMissingTranslationHandler)
    }),
    provideRouterStore(),
    provideStore({
      pagesState: mainFilterReducer,
      routerState: routerReducer,
      breeceSelectorState: breeceSelectorReducer,
      generalState: generalStateReducere,
      securityState: securityStateReducere,
      profileState: profileStateReducere,
      tableState: tableStateReducer,
      installationState: installationStateReducer
    }),
    provideEffects([
      GeneralStateEffects,
      ListFeatureEffects,
      BreeceSelectorEffects,
      SecurityStateEffects,
      ProfileStateEffect,
      InstallationStateEffects,
      RouterEffects,
      TableFilterSetEffects,
      TableFilterEffects,
      TableViewEffects
    ]),
    provideStoreDevtools({
      name: 'BreeceCloudFrontend v1',
      maxAge: 100,
      logOnly: !isDevMode()
    }),
    provideAuth({
      config: {
        authority: environment.authority,
        redirectUrl: environment.redirectUrl,
        postLogoutRedirectUri: environment.postLogoutRedirectUri,
        clientId: 'angulargui',
        scope: environment.authorityScope,
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        ignoreNonceAfterRefresh: true, // this is required if the id_token is not returned
        triggerRefreshWhenIdTokenExpired: false, // required when refreshing the browser if id_token is not updated after the first authentication
        // allowUnsafeReuseRefreshToken: true, // this is required if the refresh token is not rotated
        autoUserInfo: false, // if the user endpoint is not supported
        // logLevel: LogLevel.Debug,
      }
    }),
    provideDateFnsAdapter(),
    provideAppInitializer(() => {
      const initializerFn = (languageInitializerFactory)(inject(TranslateService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (customMatIconsInitializerFactory)(inject(MatIconRegistry), inject(DomSanitizer));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = (AuthenticationFactory)(inject(OidcSecurityService), inject(AuthTokenService), inject(Store), inject(Router));
      return initializerFn();
    }),
  ]
};



